import { Controller } from "@hotwired/stimulus"

// The /learn browse rail's two client-side affordances: a search field that
// filters the rendered feed, and a "show more" that collapses its tail.
//
// Both are progressive enhancement, and deliberately so. Every entry is in the
// server-rendered HTML — this controller only hides things — so crawlers, the
// ItemList JSON-LD, and anyone without JS still get the whole drawer. The
// search field and the button therefore ship `hidden` and are revealed here;
// a search box that does nothing is worse than no search box.
//
// Filtering is substring matching over the text already on the page, not a
// query to the server. At the scale this feed runs at (tens of articles, all
// in the DOM) that's the honest implementation, and it stays instant. If the
// corpus outgrows one page, this becomes a real search endpoint.

// The lead counts as one, so this is the lead plus ten rows.
const INITIAL_ENTRIES = 11

export default class extends Controller {
  static targets = ["search", "query", "entry", "more", "moreButton", "noResults"]

  connect() {
    // Text is cached per element rather than on a data attribute: the same
    // string in the DOM twice, for every article, is a lot of bytes to spend
    // on a lookup table.
    this.haystacks = new WeakMap()
    if (this.hasSearchTarget) this.searchTarget.hidden = false
    this.visibleCount = INITIAL_ENTRIES
    this.render()
  }

  filter() {
    // A search spans the whole feed: re-collapsing the tail would hide
    // matches the visitor just asked for.
    this.visibleCount = this.query ? Infinity : INITIAL_ENTRIES
    this.render()
  }

  showMore() {
    const firstHidden = this.entryTargets.find((entry) => entry.hidden)
    this.visibleCount += INITIAL_ENTRIES
    this.render()
    // Once the button has no job left it disappears, taking the keyboard's
    // place in the page with it — hand focus to what was just revealed.
    if (this.hasMoreTarget && this.moreTarget.hidden) {
      firstHidden?.querySelector("a")?.focus()
    }
  }

  render() {
    const query = this.query
    let shown = 0
    let matched = 0

    this.entryTargets.forEach((entry) => {
      const hit = !query || this.haystack(entry).includes(query)
      if (hit) matched += 1
      const visible = hit && shown < this.visibleCount
      if (visible) shown += 1
      entry.hidden = !visible
    })

    const remaining = matched - shown
    if (this.hasMoreTarget) {
      this.moreTarget.hidden = remaining <= 0
      if (this.hasMoreButtonTarget && remaining > 0) {
        this.moreButtonTarget.textContent = `Show ${remaining} more`
      }
    }
    if (this.hasNoResultsTarget) this.noResultsTarget.hidden = matched > 0
  }

  get query() {
    return this.hasQueryTarget ? this.queryTarget.value.trim().toLowerCase() : ""
  }

  haystack(entry) {
    let text = this.haystacks.get(entry)
    if (text === undefined) {
      text = entry.textContent.replace(/\s+/g, " ").toLowerCase()
      this.haystacks.set(entry, text)
    }
    return text
  }
}
